import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../../config';
import AdminDashboardStats from './AdminDashboardStats';
import AdminProducts from './AdminProducts';
import AdminCategories from './AdminCategories';
import AdminOrders from './AdminOrders';
import AdminCustomers from './AdminCustomers';
import AdminHeroImages from './AdminHeroImages';
import AdminSections from './AdminSections';
import CollectionBannerTab from './CollectionBannerTab';
import AdminCoupons from './AdminCoupons';
import AdminLocations from './AdminLocations';
import AdminBlogs from './AdminBlogs';
import AdminHeader from './AdminHeader';
import AdminFooter from './AdminFooter';
import {
    Squares2X2Icon,
    ShoppingBagIcon,
    TagIcon,
    ClipboardDocumentListIcon,
    UserGroupIcon,
    PhotoIcon,
    ViewColumnsIcon,
    TicketIcon,
    Cog6ToothIcon,
    Bars3Icon,
    XMarkIcon,
    MapPinIcon,
    DocumentTextIcon,
    ArrowRightStartOnRectangleIcon
} from '@heroicons/react/24/outline';

import { useAppContext } from '../../context/AppContext';
import logo from '../../images/FINAL-DESIGN-LOGO.png';

const AdminDashboard = () => {
    const { user, logout } = useAppContext();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [stats, setStats] = useState({});
    const [heroImages, setHeroImages] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    useEffect(() => {
        fetchHeroImagesPreview();
        fetchDashboardStats();
    }, []);

    // Close sidebar when tab changes on mobile
    useEffect(() => {
        if (window.innerWidth < 1024) {
            setIsSidebarOpen(false);
        }
    }, [activeTab]);

    const fetchHeroImagesPreview = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/hero-images`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHeroImages(response.data || []);
        } catch (error) {
            // silent fail for preview
        }
    };

    const fetchDashboardStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/dashboard/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data || {});
        } catch (error) {
            setStats({});
            toast.error('Failed to fetch dashboard stats');
        }
    };

    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-gray-50 pt-20 pb-12 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h2>
                    <p className="text-gray-600">You need admin privileges to access this page.</p>
                </div>
            </div>
        );
    }

    const tabs = [
        { id: 'dashboard', name: 'Overview', icon: <Squares2X2Icon className="h-5 w-5" /> },
        { id: 'products', name: 'Products', icon: <ShoppingBagIcon className="h-5 w-5" /> },
        { id: 'categories', name: 'Categories', icon: <TagIcon className="h-5 w-5" /> },
        { id: 'blogs', name: 'Blog Posts', icon: <DocumentTextIcon className="h-5 w-5" /> },
        { id: 'customers', name: 'Customers', icon: <UserGroupIcon className="h-5 w-5" /> },
        { id: 'heroImages', name: 'Hero Images', icon: <PhotoIcon className="h-5 w-5" /> },
        { id: 'sections', name: 'Sections', icon: <ViewColumnsIcon className="h-5 w-5" /> },
        { id: 'locations', name: 'Store Locations', icon: <MapPinIcon className="h-5 w-5" /> },
        { id: 'footer', name: 'Footer Settings', icon: <DocumentTextIcon className="h-5 w-5" /> },
    ];

    return (
        <div className="flex min-h-screen bg-[#F3F4F6]">
            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
                w-64 bg-[#121212] text-gray-300 flex flex-col h-screen lg:min-h-screen
                transform transition-transform duration-300 ease-in-out
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="h-24 flex items-center justify-between px-6 border-b border-white/10">
                    <img src={logo} alt="SR Daily Nutrition" className="h-12 w-auto" />
                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        className="lg:hidden text-gray-400 hover:text-white"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
                    <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Menu</p>
                    {tabs.map((tab, index) => (
                        <motion.button
                            key={tab.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group ${activeTab === tab.id
                                ? 'bg-[#B91C1C] text-white shadow-lg shadow-red-900/20'
                                : 'hover:bg-white/5 hover:text-white text-gray-400'
                                }`}
                        >
                            <span className={`mr-3 transition-colors ${activeTab === tab.id ? 'text-white' : 'text-gray-500 group-hover:text-white'}`}>
                                {tab.icon}
                            </span>
                            <span className="font-medium">{tab.name}</span>
                        </motion.button>
                    ))}

                    <div className="pt-4 mt-4 border-t border-gray-800">
                        <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Main Site</p>
                        <a href="/" className="w-full flex items-center px-4 py-3 text-gray-400 hover:text-white hover:bg-[#1F1F23] rounded-xl transition-all">
                            <span className="mr-3"><ShoppingBagIcon className="h-5 w-5" /></span>
                            <span className="font-medium">Home</span>
                        </a>
                    </div>
                </nav>

                <div className="p-4 border-t border-gray-800 pb-24 lg:pb-4">
                    <button
                        onClick={() => {
                            logout();
                            navigate('/');
                        }}
                        className="w-full flex items-center px-4 py-3 text-gray-400 hover:text-white hover:bg-red-500/10 rounded-xl transition-all"
                    >
                        <ArrowRightStartOnRectangleIcon className="h-5 w-5 mr-3" />
                        <span className="font-medium">Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col min-h-screen w-full lg:w-auto">
                {/* Mobile Header */}
                <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                        <Bars3Icon className="h-6 w-6" />
                    </button>
                    <span className="font-bold text-gray-900">Admin Panel</span>
                    <div className="w-10" /> {/* Spacer for centering */}
                </div>

                <div className="hidden lg:block">
                    <AdminHeader user={user} />
                </div>

                <div className="flex-1 p-4 lg:p-8 overflow-y-auto overflow-x-hidden">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            {activeTab === 'dashboard' && <AdminDashboardStats stats={stats} setActiveTab={setActiveTab} />}
                            {activeTab === 'products' && <AdminProducts />}
                            {activeTab === 'categories' && <AdminCategories />}
                            {activeTab === 'blogs' && <AdminBlogs />}
                            {activeTab === 'orders' && <AdminOrders />}
                            {activeTab === 'customers' && <AdminCustomers />}
                            {activeTab === 'heroImages' && <AdminHeroImages />}
                            {activeTab === 'sections' && <AdminSections />}
                            {activeTab === 'collectionBanner' && <CollectionBannerTab />}
                            {activeTab === 'coupons' && <AdminCoupons />}
                            {activeTab === 'locations' && <AdminLocations />}
                            {activeTab === 'footer' && <AdminFooter />}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;
